import React, { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  DatePickerWithRange,
  TimePicker,
} from 'ui'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { PlusCircle, Trash2 } from 'lucide-react'
import { Event } from '../../../queries/types'
import { useUpdateEventMutation } from '../../../queries/useUpdateEventMutation'
import toast from 'react-hot-toast'

// Validation schema
const schema = yup.object({
  dates: yup
    .array(
      yup.object({
        start: yup.date().optional(),
        end: yup.date().optional(),
      })
    )
    .optional(),
  times: yup
    .array(
      yup.object({
        start: yup.string().required(),
        end: yup.string().required(),
      })
    )
    .optional(),
  flexibleDates: yup.boolean().optional(),
  flexibleTimes: yup.boolean().optional(),
  meeting_options: yup.array().of(yup.string().required()).optional(),
  street_address: yup.string().optional(),
  virtual_meeting_url: yup.string().url('Please enter a valid URL').optional(),
})
export type EventStepTwo = yup.InferType<typeof schema>

interface CreateEventStepTwoFormProps {
  eventId: string
  existingEvent?: Event
  isLoading?: boolean
}

export const CreateEventStepTwoForm: React.FC<CreateEventStepTwoFormProps> = ({
  eventId,
  existingEvent,
  isLoading,
}) => {
  const navigate = useNavigate()
  const updateEventMutation = useUpdateEventMutation()

  // Check for saved form data in localStorage
  const savedFormData = localStorage.getItem('eventStepOne')
  const parsedLocalStorage = savedFormData ? JSON.parse(savedFormData) : null

  // Initialize form with either existing event data, localStorage data, or defaults
  const form = useForm<EventStepTwo>({
    defaultValues: {
      dates: existingEvent?.dates?.dates?.length
        ? existingEvent.dates.dates.map((date) => ({
            start: date.startDate ? new Date(date.startDate) : undefined,
            end: date.endDate ? new Date(date.endDate) : undefined,
          }))
        : [{ start: undefined, end: undefined }],
      times: existingEvent?.dates?.times?.length
        ? existingEvent.dates.times.map((time) => ({
            start: time.start || '',
            end: time.end || '',
          }))
        : [],
      flexibleDates: existingEvent?.flexible_dates || false,
      flexibleTimes: existingEvent?.flexible_times || false,
      meeting_options: Array.isArray(existingEvent?.connection_preference)
        ? existingEvent.connection_preference
        : [],
      street_address: existingEvent?.location?.address || '',
      virtual_meeting_url: existingEvent?.location?.url || '',
    } as EventStepTwo,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  // Initialize/refresh form data when existingEvent changes
  useEffect(() => {
    if (existingEvent) {
      // Reset form with latest event data when it changes
      form.reset({
        dates: existingEvent.dates?.dates?.length
          ? existingEvent.dates.dates.map((date) => ({
              start: date.startDate ? new Date(date.startDate) : undefined,
              end: date.endDate ? new Date(date.endDate) : undefined,
            }))
          : [{ start: undefined, end: undefined }],
        times: existingEvent.dates?.times?.length
          ? existingEvent.dates.times.map((time) => ({
              start: time.start || '',
              end: time.end || '',
            }))
          : [],
        flexibleDates: existingEvent.flexible_dates || false,
        flexibleTimes: existingEvent.flexible_times || false,
        meeting_options: Array.isArray(existingEvent.connection_preference)
          ? existingEvent.connection_preference
          : [],
        street_address: existingEvent.location?.address || '',
        virtual_meeting_url: existingEvent.location?.url || '',
      })
    }
  }, [existingEvent, form])

  const handleSubmitForm = (
    redirect: string = '/app/events/{id}/three'
  ) => async (values: EventStepTwo) => {
    try {
      // Update the event with step two data
      if (eventId !== 'new') {
        // Map form data to API format
        const updateData = {
          id: eventId,
          flexible_dates: values.flexibleDates,
          flexible_times: values.flexibleTimes,
          dates: {
            dates: values.dates?.map((date) => ({
              start: date.start?.toISOString() ?? '',
              end: date.end?.toISOString() ?? '',
            })),
            times: values.times,
          },
          connection_preference: values.meeting_options,
          location: {
            address: values.street_address,
            url: values.virtual_meeting_url,
          },
          status: 'DRAFT',
        }

        await updateEventMutation.mutateAsync(updateData)
        toast.success('Event updated successfully')
      }

      // Handle dynamic redirect with event ID
      const targetRedirect = redirect.includes('{id}')
        ? redirect.replace('{id}', eventId)
        : redirect

      navigate(targetRedirect)
    } catch (error) {
      console.error('Error updating event:', error)
      toast.error('There was an error updating the event')
    }
  }

  const onSaveDraft = async () => {
    await form.handleSubmit(handleSubmitForm('/app/dashboard'))()
  }

  const meetingOptions = [
    'In-person',
    'Remote (Zoom, teams, etc.)',
    'I have a location in mind',
    'Location is flexible (even if I have one in mind)',
  ]

  // Initialize useFieldArray for dates
  const {
    fields: dateFields,
    append: appendDate,
    remove: removeDate,
  } = useFieldArray({
    control: form.control,
    name: 'dates',
  })

  // Initialize useFieldArray for times
  const {
    fields: timeFields,
    append: appendTime,
    remove: removeTime,
  } = useFieldArray({
    control: form.control,
    name: 'times',
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm())}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>Dates</FormLabel>
          <p className="text-body font-body text-sm mb-4">
            Choose all that apply. Keep in mind that invitations with specific
            dates and times are more likely to be accepted by those you invite.
            If you're unsure, please choose dates and times that are in the
            ballpark and check the flexible boxes below.
          </p>

          {dateFields.map((field, index) => (
            <div key={field.id} className="flex items-start mb-4">
              <FormField
                control={form.control}
                name={`dates.${index}`}
                render={({ field }) => {
                  return (
                    <FormItem className="flex-grow">
                      <FormControl>
                        <DatePickerWithRange
                          value={
                            field.value?.start || field.value?.end
                              ? {
                                  from: field.value.start,
                                  to: field.value.end,
                                }
                              : undefined
                          }
                          fromDate={new Date()}
                          onChange={(range) => {
                            // When user selects a date when both start and end are already defined,
                            // it should reset to a new range starting with the selected date
                            if (
                              field.value?.start &&
                              field.value?.end &&
                              range?.from &&
                              !range?.to
                            ) {
                              field.onChange({
                                start: range.from,
                                end: undefined,
                              })
                            }
                            // Normal case - both dates provided
                            else if (range?.from && range?.to) {
                              field.onChange({
                                start: range.from,
                                end: range.to,
                              })
                            }
                            // Only start date provided
                            else if (range?.from) {
                              field.onChange({
                                start: range.from,
                                end: undefined,
                              })
                            }
                            // No dates provided
                            else {
                              field.onChange({
                                start: undefined,
                                end: undefined,
                              })
                            }
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              {index > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-2"
                  onClick={() => removeDate(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="default"
            size="sm"
            className="-mt-2 self-start"
            onClick={() => appendDate({ start: undefined, end: undefined })}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Date Range
          </Button>

          <FormField
            control={form.control}
            name="flexibleDates"
            render={({ field }) => (
              <div className="mt-1">
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormLabel className="text-sm ml-1 text-body text-body/70">
                    Dates are flexible
                  </FormLabel>
                </FormItem>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col mt-4">
          <FormLabel className="mb-2">Times</FormLabel>
          {timeFields.map((field, index) => (
            <div
              key={field.id}
              className="flex items-center gap-4 grid grid-cols-6"
            >
              <FormField
                control={form.control}
                name={`times.${index}.start`}
                render={({ field }) => (
                  <FormItem className="flex-1 col-span-2">
                    <FormLabel className="text-xs hidden">Start Time</FormLabel>
                    <FormControl>
                      <TimePicker
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isLoading || form.formState.isSubmitting}
                        placeholder="Start time"
                        minuteStep={15}
                        use24Hours={false}
                        minTime="06:00"
                        maxTime="20:00"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`times.${index}.end`}
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel className="text-xs hidden">End Time</FormLabel>
                    <FormControl>
                      <TimePicker
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isLoading || form.formState.isSubmitting}
                        placeholder="End time"
                        minuteStep={15}
                        use24Hours={false}
                        minTime="06:00"
                        maxTime="20:00"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="self-end mb-1"
                onClick={() => removeTime(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="default"
            size="sm"
            className="mt-1 self-start mb-4"
            onClick={() => appendTime({ start: '', end: '' })}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Time Slot
          </Button>

          <FormField
            control={form.control}
            name="flexibleTimes"
            render={({ field }) => (
              <div className="-mt-2">
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormLabel className="text-sm ml-1 text-body text-body/70">
                    Times are flexible
                  </FormLabel>
                </FormItem>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>In Person or Remote</FormLabel>
          <p className="font-body text-sm mt-1">
            Choose all that apply. There's no wrong answer.
          </p>
          <FormField
            control={form.control}
            name="meeting_options"
            render={({ field: { value, onChange } }) => (
              <div className="mt-4">
                {meetingOptions.map((expectation) => (
                  <FormItem
                    key={expectation}
                    className="flex items-start space-x-2 mt-2"
                  >
                    <FormControl>
                      <Checkbox
                        className="mt-1"
                        checked={
                          Array.isArray(value) && value.includes(expectation)
                        }
                        onCheckedChange={(checked) => {
                          const currentValue = Array.isArray(value) ? value : []
                          if (checked) {
                            onChange([...currentValue, expectation])
                          } else {
                            onChange(
                              currentValue.filter((e) => e !== expectation)
                            )
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-1 text-body">
                      {expectation}{' '}
                      <span
                        className="text-body/50 cursor-help"
                        title="More information about this option"
                      >
                        ⓘ
                      </span>
                    </FormLabel>
                  </FormItem>
                ))}

                {/* Conditional Street Address Field */}
                {value?.includes('In-person') && (
                  <FormField
                    control={form.control}
                    name="street_address"
                    render={({ field }) => (
                      <FormItem className="mt-4 ml-8">
                        <FormLabel>Street Address (optional)</FormLabel>
                        <FormControl>
                          <input
                            {...field}
                            className="w-full p-2 border rounded"
                            placeholder="Enter a street address"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Conditional Virtual Meeting URL Field */}
                {value?.includes('Remote (Zoom, teams, etc.)') && (
                  <FormField
                    control={form.control}
                    name="virtual_meeting_url"
                    render={({ field }) => (
                      <FormItem className="mt-4 ml-8">
                        <FormLabel>Virtual Meeting URL (optional)</FormLabel>
                        <FormControl>
                          <input
                            {...field}
                            className="w-full p-2 border rounded"
                            placeholder="https://..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}
          />
        </div>

        <div className="flex-col flex mt-12 w-full">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            variant="default"
            className="mx-auto uppercase"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>Next: Keywords &gt;</span>
          </Button>
          <Button
            type="button"
            variant="link"
            className="text-actionable underline text-sm"
            onClick={onSaveDraft}
          >
            <span>Save Draft and Finish Later</span>
          </Button>
        </div>
      </form>
    </Form>
  )
}
